import { useState } from 'react';
import { Store } from 'lucide-react';
import { useAccountBusinessUnits } from '@/hooks/use-business-units';
import { FilterOption, FilterPill } from '@/components/ui/filter-pill';

interface BrandScopePickerProps {
  accountId: number;
  /** Picked brand id, or null for the whole account. */
  value: string | null;
  onChange: (buId: string | null) => void;
}

/** Brand scope for the account on screen; hidden when it has a single brand, since "All brands" would be the same rows. */
export function BrandScopePicker({ accountId, value, onChange }: BrandScopePickerProps) {
  const [open, setOpen] = useState(false);
  const units = useAccountBusinessUnits(accountId);
  if (!units || units.length < 2) return null;

  const picked = units.find((bu) => String(bu.id) === value);
  const pick = (buId: string | null) => {
    onChange(buId);
    setOpen(false);
  };

  return (
    <FilterPill
      label={picked ? picked.name : 'All brands'}
      icon={<Store aria-hidden='true' className='size-3.5 shrink-0' />}
      contentClassName='w-[240px]'
      open={open}
      onOpenChange={setOpen}
    >
      <div role='listbox' aria-label='Brands' className='custom-scrollbar flex max-h-[320px] min-h-0 flex-col gap-0.5 overflow-y-auto'>
        <FilterOption label='All brands' selected={!picked} onSelect={() => pick(null)} />
        {/* Inactive brands keep their history, so they stay pickable. */}
        {units.map((bu) => (
          <FilterOption
            key={bu.id}
            label={bu.name}
            hint={bu.status === 'inactive' ? 'Inactive' : undefined}
            selected={picked?.id === bu.id}
            onSelect={() => pick(String(bu.id))}
          />
        ))}
      </div>
    </FilterPill>
  );
}
